import { useState } from "react";
import type { CompilationTheme } from "@memento/types/compilation-theme";
import type { CompilationOccasionId } from "@memento/types/compilation-occasion";
import type { RenderConfig } from "@memento/types/render-config";
import { getIsRenderConfigUserInputComplete } from "@memento/library/compilation";
import { type SetEditor } from "src/components/Renderer/RendererProvider";
import { generatePrefetchUrls, prefetchAssetsUrls } from "../prefetchAssets";
import { getCompositionHasFaceCollage } from "src/components/Renderer/Pages/getCompositionHasFaceCollage";
import { useCompilationTheme } from "src/components/Renderer/Pages/Theme/useCompilationTheme";

export type UseThemeType = ReturnType<typeof useTheme>;

export const useTheme = (editor: RenderConfig, setEditor: SetEditor) => {
  const [isPreFetching, setIsPreFetching] = useState(false);

  const { theme } = useCompilationTheme(editor.themeId);

  // The theme should have been loaded before this hook is called
  if (!theme) {
    throw new Error(`Theme not found: ${editor.themeId}`);
  }

  function setTheme(theme: CompilationTheme) {
    setEditor((prev) => ({
      ...prev,
      themeId: theme.themeId,
    }));

    preFetchThemeAssets(theme);
  }

  function preFetchThemeAssets(theme: CompilationTheme) {
    setIsPreFetching(true);

    const fetchAssets = async () => {
      const assetUrls = generatePrefetchUrls(theme);
      await prefetchAssetsUrls(assetUrls);
      setIsPreFetching(false);
    };

    fetchAssets();
  }

  function setOccasionId(occasionId: CompilationOccasionId) {
    setEditor((prev) => ({
      ...prev,
      occasionId,
    }));
  }

  function setThemeUserInput(id: string, defaultValue: string) {
    setEditor((prev) => ({
      ...prev,
      userInput: { ...prev.userInput, [id]: defaultValue },
    }));
  }

  function setFaceCollageEnabled(enabled: boolean) {
    setEditor((prev) => ({
      ...prev,
      faceCollageEnabled: enabled,
    }));
  }

  const isThemeUserInputComplete = getIsRenderConfigUserInputComplete(
    editor,
    theme
  );

  const themeHasUserInputFields = Boolean(
    (theme?.userInputFields && theme.userInputFields.length > 0) ||
    getCompositionHasFaceCollage(theme)
  );

  return {
    preFetchThemeAssets,
    theme,
    setTheme,
    setOccasionId,
    isPreFetching,
    setThemeUserInput,
    isThemeUserInputComplete,
    themeHasUserInputFields,
    setFaceCollageEnabled,
    themeThumbnailFrame: theme?.thumbnailFrame || 0,
  };
};
